'use client';

import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import styled from '@emotion/styled';
import { ButtonSize, ButtonType } from 'constants/button';
import { ModalParams, ModalType } from 'constants/modal';
import { useTrackingState } from 'contexts/TrackingProvider';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'ui-components/common/Button';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { useShowModal } from 'ui-hooks/useShowModal';
import { ModalHandler } from 'utils/ModalHandler';

export const AppPushOnBottomModal = () => {
  const { tracker } = useTrackingState();
  const { isShow, revealModal, showModal, hideModal } = useShowModal();
  const [state, setState] = useState<ModalParams[ModalType.AppPushOn]>();

  const handleClickPushOn = useCallback(() => {
    state?.onClickPushOn?.();
    tracker.track(TrackingEventType.ClickAppPushOnBottomModal);
    DirittoAppBridge.changePushOn(true);
    hideModal();
  }, [hideModal, state, tracker]);

  const handleClose = useCallback(() => {
    state?.onCancel?.();
    tracker.track(TrackingEventType.CloseAppPushOnBottomModal);
    hideModal();
  }, [hideModal, state, tracker]);

  useEffect(() => {
    const listener = (show: boolean, params?: ModalParams[ModalType.AppPushOn]) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
      setState(params);
    };

    ModalHandler.addEventListener(ModalType.AppPushOn, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.AppPushOn);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal} isBottom>
      <StyledModal>
        <StyledTitle>{state?.title}</StyledTitle>
        <StyledDescription>{state?.description || ''}</StyledDescription>
        <StyledButtons>
          <StyledButton label="기기 알림 설정하러 가기" size={ButtonSize.Medium} onClick={handleClickPushOn} />
          <StyledButton label="닫기" buttonType={ButtonType.SecondaryNoBorder} onClick={handleClose} />
        </StyledButtons>
      </StyledModal>
    </ModalLayout>
  );
};

const StyledModal = styled.div`
  width: 100vw;
  max-height: 227px;
  background-color: ${Color.White};
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 32px;
  padding-right: 16px;
  padding-left: 16px;
  ${SafeAreaBottomStyles(16)}
`;

const StyledTitle = styled.p`
  font-weight: ${FontWeight.Bold};
  ${Typography.Medium};
  color: ${Color.Black};
`;

const StyledDescription = styled.div`
  text-align: center;
  margin-top: 4px;
  ${Typography.XXSmall};
  color: ${Color.Gray600};
`;

const StyledButtons = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  width: 100%;
`;
